import { useMemo } from "react";
import type { Region } from "@typedoc/contracts";
import type { AppError } from "../app-error";
import { regionalOptions } from "../access-client";
import type { RegionalOptions, RegionalSession } from "../regional-request";
import type { AccessScope } from "../regional-token-cache";
import { readWorkspaceScope, useWorkspaceScope, type WorkspaceScope } from "../workspace-scope";
import type { WorkspacesResponse } from "../workspaces";

/**
 * Every connections call: the shared options with the longer bound the regional API may need (a
 * site preview waits on someone else's server). Bound here once; the calls take the session.
 */
export const connectionsOptions: RegionalOptions = { ...regionalOptions, timeoutMs: 15_000 };

/** A placeholder while the session loads; queries keyed by it stay disabled until a real scope lands. */
export const NO_SCOPE: AccessScope = { userId: "", workspaceId: "" };

/** The placeholder session for those queries: it is never sent, because they are disabled. */
export const NO_SESSION: RegionalSession = { scope: NO_SCOPE, options: connectionsOptions };

/** What every connections screen needs once the session has loaded: the session, and its region. */
export type ConnectionsContext = RegionalSession & { region: Region };

/**
 * Why there is no context yet, once the answer is known. Without it every connections screen
 * could only spin: a session with no active workspace, or a workspace listing that failed, looks
 * the same as one still loading.
 */
export type ScopeBlocker =
  | { kind: "no-workspace" }
  | { kind: "failed"; error: AppError; retry: () => void };

/** The connections screens' view of the workspace scope: a context once known, else why not. */
export function connectionsScopeFrom({
  userId,
  data,
  error,
  settling,
  refetch,
}: {
  userId: string;
  data: WorkspacesResponse | undefined;
  error: unknown;
  settling: boolean;
  refetch: () => void;
}): { context: ConnectionsContext | null; blocker: ScopeBlocker | null } {
  return viewOf(readWorkspaceScope({ userId, data, error, settling }), refetch);
}

function viewOf(
  found: WorkspaceScope,
  retry: () => void,
): { context: ConnectionsContext | null; blocker: ScopeBlocker | null } {
  if (found.ready && found.region) {
    const scope = { userId: found.userId, workspaceId: found.workspaceId };
    return { context: { scope, region: found.region, options: connectionsOptions }, blocker: null };
  }
  if (found.noWorkspace) return { context: null, blocker: { kind: "no-workspace" } };
  if (found.failure) {
    return { context: null, blocker: { kind: "failed", error: found.failure, retry } };
  }
  return { context: null, blocker: null };
}

/** Who and where: the signed-in user and the session's active workspace and region. */
export function useConnectionsScope() {
  const { userId, workspaceId, region, ready, failure, noWorkspace, retry } = useWorkspaceScope();
  // The context keeps its identity while the same workspace is read again.
  return useMemo(
    () => viewOf({ userId, workspaceId, region, ready, failure, noWorkspace }, retry),
    [userId, workspaceId, region, ready, failure, noWorkspace, retry],
  );
}
